(() => {
  const FIELDS =
    'id,caption,media_type,media_url,permalink,thumbnail_url,timestamp,children{id,media_type,media_url,thumbnail_url}';
  const PRODUCT_JSON = '/products.json?limit=250';

  const roots = document.querySelectorAll('[data-beau-ig]');
  if (!roots.length) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const tagCache = new Map();
  let catalogPromise = null;
  let activeModal = null;

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const norm = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const money = (value) => {
    const n = parseFloat(value);
    if (Number.isNaN(n)) return '';
    return '$' + (Number.isInteger(n) ? String(n) : n.toFixed(n % 1 === 0 ? 0 : 2).replace(/\.00$/, ''));
  };

  const mediaOf = (item) => {
    if (!item) return { id: '', type: 'IMAGE', image: '', video: '' };
    if (item.media_type === 'VIDEO') {
      return {
        id: item.id || '',
        type: 'VIDEO',
        image: item.thumbnail_url || '',
        video: item.media_url || '',
      };
    }
    return {
      id: item.id || '',
      type: 'IMAGE',
      image: item.media_url || item.thumbnail_url || '',
      video: '',
    };
  };

  const slidesOf = (item) => {
    if (item && item.media_type === 'CAROUSEL_ALBUM' && item.children && item.children.data) {
      const slides = item.children.data.map(mediaOf);
      return slides.length ? slides : [mediaOf(item)];
    }
    return [mediaOf(item)].filter((slide) => slide.image || slide.video || slide.id);
  };

  const graphHost = (token, user) => {
    if (String(token).indexOf('IG') === 0 || user === 'me') return 'https://graph.instagram.com/';
    return 'https://graph.facebook.com/v26.0/';
  };

  const fetchAll = async (startUrl, limit) => {
    const items = [];
    let url = startUrl;
    while (url && items.length < limit) {
      const res = await fetch(url);
      const json = await res.json();
      if (json.error) throw new Error(json.error.message || 'Instagram API error');
      if (Array.isArray(json.data)) items.push(...json.data);
      url = json.paging && json.paging.next ? json.paging.next : '';
    }
    return items.slice(0, limit);
  };

  const shopifyIdFromRetailer = (value) => {
    const text = String(value || '');
    const prefixed = text.match(/shopify_[a-z]+_(\d+)(?:_\d+)?/i);
    if (prefixed) return prefixed[1];
    const plain = text.match(/^(\d{8,})(?:_\d+)?$/);
    return plain ? plain[1] : '';
  };

  const handleFromUrl = (value) => {
    const match = String(value || '').match(/\/products\/([a-z0-9-]+)/i);
    return match ? match[1] : '';
  };

  const parseCatalog = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((product) => ({
        id: String(product.id || ''),
        title: product.title || '',
        handle: product.handle || '',
        image: product.image || '',
        price: product.price || '',
        variant_ids: (product.variant_ids || []).map(String),
      }))
      .filter((product) => product.handle);
  };

  const loadCatalog = (root) => {
    if (!catalogPromise) {
      const embedded = root && root.querySelector('[data-beau-ig-catalog]');
      let local = [];
      if (embedded) {
        try {
          local = parseCatalog(JSON.parse(embedded.textContent || '[]'));
        } catch (err) {
          local = [];
        }
      }
      catalogPromise = local.length
        ? Promise.resolve(local)
        : fetch(PRODUCT_JSON)
            .then((res) => res.json())
            .then((json) =>
              parseCatalog(
                (json.products || []).map((product) => ({
                  id: product.id,
                  title: product.title,
                  handle: product.handle,
                  image: product.images && product.images[0] ? product.images[0].src : '',
                  price: product.variants && product.variants[0] ? product.variants[0].price : '',
                }))
              )
            )
            .catch(() => []);
    }
    return catalogPromise;
  };

  const fetchTags = (host, token, mediaId) => {
    if (!mediaId || !token) return Promise.resolve([]);
    if (tagCache.has(mediaId)) return tagCache.get(mediaId);
    const req = fetch(
      host + encodeURIComponent(mediaId) + '/product_tags?access_token=' + encodeURIComponent(token)
    )
      .then((res) => res.json())
      .then(async (json) => {
        const tags = Array.isArray(json.data) ? json.data : [];
        await Promise.all(
          tags.map((tag) => {
            if (!tag.product_id) return null;
            return fetch(
              host +
                encodeURIComponent(tag.product_id) +
                '?fields=id,name,retailer_id,url&access_token=' +
                encodeURIComponent(token)
            )
              .then((res) => res.json())
              .then((info) => {
                if (info && !info.error) {
                  tag.retailer_id = info.retailer_id || tag.retailer_id;
                  tag.catalog_url = info.url || tag.catalog_url;
                  if (!tag.name && info.name) tag.name = info.name;
                }
              })
              .catch(() => {});
          })
        );
        return tags;
      })
      .catch(() => []);
    tagCache.set(mediaId, req);
    return req;
  };

  const garmentOf = (title) => {
    const t = norm(title);
    if (/\bone pieces?\b/.test(t)) return 'onepiece';
    if (/\bwrap\b/.test(t)) return 'wrap';
    if (/\bshorts?\b/.test(t)) return 'short';
    if (/\btote\b/.test(t)) return 'tote';
    if (/\bheadband\b/.test(t)) return 'headband';
    if (/\bbikini\b/.test(t) || /\btops?\b/.test(t) || /\bbottoms?\b/.test(t)) return 'bikini';
    return '';
  };

  const firstName = (title) => norm(title).split(' ')[0] || '';

  const familyProducts = (catalog, name, garment) =>
    catalog.filter((product) => {
      if (firstName(product.title) !== name) return false;
      if (!garment) return true;
      return garmentOf(product.title) === garment;
    });

  const matchFromCaption = (catalog, caption, add) => {
    const text = norm(caption);
    if (!text) return;

    catalog
      .slice()
      .sort((a, b) => b.title.length - a.title.length)
      .forEach((product) => {
        const title = norm(product.title);
        if (title.length < 8) return;
        if (text.indexOf(title) !== -1) add(product);
      });

    const names = Array.from(
      new Set(
        catalog.map((product) => firstName(product.title)).filter((name) => name.length > 2 && name !== 'beau')
      )
    );
    if (!names.length) return;

    const pair = new RegExp(
      '\\b(' +
        names.join('|') +
        ')\\s+(bikini(?:\\s+sets?)?|sets?|one\\s*pieces?|wraps?|shorts?|totes?|headbands?)\\b',
      'g'
    );
    let match;
    while ((match = pair.exec(text))) {
      const name = match[1];
      const kind = match[2];
      let garment = 'bikini';
      if (/one/.test(kind) && /piece/.test(kind)) garment = 'onepiece';
      else if (/wrap/.test(kind)) garment = 'wrap';
      else if (/short/.test(kind)) garment = 'short';
      else if (/tote/.test(kind)) garment = 'tote';
      else if (/headband/.test(kind)) garment = 'headband';
      else garment = 'bikini';
      familyProducts(catalog, name, garment).forEach(add);
    }
  };

  const matchProducts = (catalog, tags, caption) => {
    const found = [];
    const add = (product) => {
      if (!product || !product.handle) return;
      if (found.some((item) => item.handle === product.handle)) return;
      found.push(product);
    };
    const byId = {};
    const byHandle = {};
    catalog.forEach((product) => {
      if (product.id) byId[product.id] = product;
      if (product.handle) byHandle[product.handle] = product;
    });

    (tags || []).forEach((tag) => {
      const fromUrl = handleFromUrl(tag.catalog_url || tag.url || '');
      if (fromUrl) add(byHandle[fromUrl]);
      const shopifyId = shopifyIdFromRetailer(tag.retailer_id);
      if (shopifyId && byId[shopifyId]) add(byId[shopifyId]);
      if (tag.retailer_id) {
        const rid = String(tag.retailer_id);
        catalog.forEach((product) => {
          if ((product.variant_ids || []).indexOf(rid) !== -1) add(product);
        });
      }
      const q = norm(tag.name || '');
      if (!q) return;
      const exact = catalog.find((product) => norm(product.title) === q);
      if (exact) {
        add(exact);
        const g = garmentOf(exact.title);
        if (g === 'bikini') familyProducts(catalog, firstName(exact.title), 'bikini').forEach(add);
        return;
      }
      catalog.forEach((product) => {
        const title = norm(product.title);
        if (title.indexOf(q) !== -1 || q.indexOf(title) !== -1) add(product);
      });
      const name = q.split(' ')[0];
      if (name.length > 2) {
        const g = /\bone/.test(q) && /piece/.test(q) ? 'onepiece' : 'bikini';
        familyProducts(catalog, name, g).forEach(add);
      }
    });

    matchFromCaption(catalog, caption || '', add);
    return found;
  };

  let loadingVideos = 0;
  const waitingVideos = [];
  const MAX_VIDEOS = 2;

  const startVideo = (videoEl) => {
    if (!videoEl || !videoEl.dataset.src || videoEl.src) return;
    if (loadingVideos >= MAX_VIDEOS) {
      waitingVideos.push(videoEl);
      return;
    }
    loadingVideos += 1;
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      loadingVideos = Math.max(0, loadingVideos - 1);
      const next = waitingVideos.shift();
      if (next) startVideo(next);
    };
    videoEl.src = videoEl.dataset.src;
    videoEl.preload = 'auto';
    videoEl.addEventListener(
      'canplay',
      () => {
        videoEl.classList.add('is-ready');
        const play = videoEl.play();
        if (play && play.catch) play.catch(() => {});
        done();
      },
      { once: true }
    );
    videoEl.addEventListener('error', done, { once: true });
    videoEl.load();
  };

  const bindVideo = (tile, videoEl) => {
    if (reduced || !videoEl || !videoEl.dataset.src) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            if (videoEl.src) videoEl.pause();
            return;
          }
          startVideo(videoEl);
          if (videoEl.src) {
            const play = videoEl.play();
            if (play && play.catch) play.catch(() => {});
          }
        });
      },
      { rootMargin: '160px 0px', threshold: 0.15 }
    );
    io.observe(tile);
  };

  const badge = (kind) => {
    const el = document.createElement('span');
    el.className = 'beau-ig__badge';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      kind === 'video'
        ? '<svg viewBox="0 0 24 24" focusable="false"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>'
        : '<svg viewBox="0 0 24 24" focusable="false"><path fill="currentColor" d="M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 0h7v7h-7v-7z"/></svg>';
    return el;
  };

  const fillSlide = (stage, slide, onError) => {
    stage.innerHTML = '';
    if (!slide) return;
    if (slide.video) {
      const video = document.createElement('video');
      video.className = 'beau-ig-modal__video';
      video.src = slide.video;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.controls = false;
      video.autoplay = !reduced;
      if (slide.image) video.poster = slide.image;
      video.referrerPolicy = 'no-referrer';
      stage.appendChild(video);
      if (!reduced) {
        const play = video.play();
        if (play && play.catch) play.catch(() => {});
      }
      return;
    }
    const img = document.createElement('img');
    img.className = 'beau-ig-modal__img';
    img.alt = '';
    img.referrerPolicy = 'no-referrer';
    img.decoding = 'async';
    if (slide.image) img.src = slide.image;
    if (onError) img.addEventListener('error', onError);
    stage.appendChild(img);
  };

  const hydrateSlide = (slide, host, token) => {
    if (!slide || !slide.id || slide.image || slide.video || slide._fetching) {
      return Promise.resolve(slide);
    }
    slide._fetching = true;
    return fetch(
      host +
        encodeURIComponent(slide.id) +
        '?fields=id,media_type,media_url,thumbnail_url&access_token=' +
        encodeURIComponent(token)
    )
      .then((res) => res.json())
      .then((json) => {
        const next = mediaOf(json);
        slide.image = next.image;
        slide.video = next.video;
        slide.type = next.type;
        return slide;
      })
      .catch(() => slide);
  };

  const renderProducts = (node, products, shopRoot) => {
    node.innerHTML = '';
    if (!products.length) {
      node.hidden = true;
      return;
    }
    node.hidden = false;
    const heading = document.createElement('h3');
    heading.className = 'beau-ig-modal__shop-title';
    heading.textContent = products.length > 1 ? 'Shop this look' : 'Shop this piece';
    node.appendChild(heading);
    products.forEach((product) => {
      const link = document.createElement('a');
      link.className = 'beau-ig-modal__product';
      link.href = shopRoot + 'products/' + product.handle;
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = '';
        img.width = 72;
        img.height = 90;
        link.appendChild(img);
      }
      const meta = document.createElement('span');
      meta.className = 'beau-ig-modal__product-meta';
      const title = document.createElement('span');
      title.className = 'beau-ig-modal__product-title';
      title.textContent = product.title;
      meta.appendChild(title);
      const price = money(product.price);
      if (price) {
        const amount = document.createElement('span');
        amount.className = 'beau-ig-modal__product-price';
        amount.textContent = price;
        meta.appendChild(amount);
      }
      link.appendChild(meta);
      node.appendChild(link);
    });
  };

  const closeModal = () => {
    if (!activeModal) return;
    const video = activeModal.querySelector('video');
    if (video) video.pause();
    activeModal.remove();
    activeModal = null;
    document.body.classList.remove('beau-ig-modal-open');
    document.removeEventListener('keydown', onKey);
  };

  const onKey = (event) => {
    if (!activeModal) return;
    if (event.key === 'Escape') {
      closeModal();
      return;
    }
    if (event.key === 'ArrowRight') {
      const next = activeModal.querySelector('[data-ig-next]');
      if (next && !next.hidden) next.click();
    }
    if (event.key === 'ArrowLeft') {
      const prev = activeModal.querySelector('[data-ig-prev]');
      if (prev && !prev.hidden) prev.click();
    }
  };

  const openModal = (root, post, token, user) => {
    closeModal();
    const slides = slidesOf(post);
    if (!slides.length) return;
    const shopRoot = root.dataset.shopRoot || '/';
    const host = graphHost(token, user);
    let index = 0;

    const modal = document.createElement('div');
    modal.className = 'beau-ig-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', (post.caption || 'Instagram post').slice(0, 80));
    modal.innerHTML =
      '<div class="beau-ig-modal__backdrop" data-ig-close></div>' +
      '<div class="beau-ig-modal__dialog">' +
      '<button class="beau-ig-modal__close" type="button" data-ig-close aria-label="Close">&times;</button>' +
      '<div class="beau-ig-modal__main">' +
      '<div class="beau-ig-modal__frame">' +
      '<div class="beau-ig-modal__stage" data-ig-stage></div>' +
      '<button class="beau-ig-modal__nav beau-ig-modal__nav--prev" type="button" data-ig-prev hidden aria-label="Previous photo">‹</button>' +
      '<button class="beau-ig-modal__nav beau-ig-modal__nav--next" type="button" data-ig-next hidden aria-label="Next photo">›</button>' +
      '</div>' +
      '<p class="beau-ig-modal__caption" data-ig-caption></p>' +
      (post.permalink
        ? '<a class="beau-ig-modal__ig" href="' +
          escapeHtml(post.permalink) +
          '" target="_blank" rel="noopener">View on Instagram</a>'
        : '') +
      '</div>' +
      '<div class="beau-ig-modal__side">' +
      '<div class="beau-ig-modal__shop" data-ig-shop hidden></div>' +
      '</div></div>';

    const stage = modal.querySelector('[data-ig-stage]');
    const prev = modal.querySelector('[data-ig-prev]');
    const next = modal.querySelector('[data-ig-next]');
    const captionEl = modal.querySelector('[data-ig-caption]');
    const shopEl = modal.querySelector('[data-ig-shop]');

    captionEl.innerHTML = escapeHtml(post.caption || '').replace(/\n/g, '<br>');

    const show = (i) => {
      index = (i + slides.length) % slides.length;
      const shown = index;
      const slide = slides[index];
      const retry = () => {
        if (!slide.id || slide._retried) return;
        slide._retried = true;
        slide.image = '';
        slide.video = '';
        slide._fetching = false;
        hydrateSlide(slide, host, token).then((ready) => {
          if (activeModal === modal && index === shown) fillSlide(stage, ready, retry);
        });
      };
      fillSlide(stage, slide, retry);
      const many = slides.length > 1;
      prev.hidden = !many;
      next.hidden = !many;
      if (!slide.image && !slide.video && slide.id) retry();
    };

    prev.addEventListener('click', (event) => {
      event.stopPropagation();
      show(index - 1);
    });
    next.addEventListener('click', (event) => {
      event.stopPropagation();
      show(index + 1);
    });
    modal.addEventListener('click', (event) => {
      if (!event.target.closest('.beau-ig-modal__dialog')) closeModal();
    });
    modal.querySelectorAll('[data-ig-close]').forEach((el) => {
      el.addEventListener('click', closeModal);
    });

    document.body.appendChild(modal);
    document.body.classList.add('beau-ig-modal-open');
    activeModal = modal;
    document.addEventListener('keydown', onKey);
    show(0);
    slides.forEach((slide) => {
      const warm = (ready) => {
        if (ready && ready.image) {
          const im = new Image();
          im.referrerPolicy = 'no-referrer';
          im.src = ready.image;
        }
      };
      if (slide.image) warm(slide);
      else if (slide.id) hydrateSlide(slide, host, token).then(warm);
    });
    modal.querySelector('.beau-ig-modal__close').focus();

    const tagToken = (root.dataset.tagToken || '').trim() || token;
    Promise.all([loadCatalog(root), fetchTags(host, tagToken, post.id)]).then(([catalog, tags]) => {
      renderProducts(shopEl, matchProducts(catalog, tags, post.caption || ''), shopRoot);
    });
  };

  const fillTile = (tile, post, token, user, root, index) => {
    const slides = slidesOf(post);
    if (!slides.length) return;
    const media = slides[0];
    tile.classList.remove('beau-ig__tile--skel');
    tile.removeAttribute('aria-hidden');
    tile.tabIndex = 0;
    tile.setAttribute('aria-label', (post.caption || 'Instagram post').slice(0, 80));
    tile.replaceChildren();

    if (media.image) {
      const img = document.createElement('img');
      img.className = 'beau-ig__still';
      img.alt = '';
      img.decoding = 'async';
      img.referrerPolicy = 'no-referrer';
      if (index >= 8) img.loading = 'lazy';
      img.addEventListener(
        'load',
        () => {
          img.classList.add('is-loaded');
          setTimeout(() => img.classList.add('is-sharp'), 80);
        },
        { once: true }
      );
      img.src = media.image;
      tile.appendChild(img);
    }

    if (media.video) {
      if (!reduced) {
        const video = document.createElement('video');
        video.className = 'beau-ig__video';
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.preload = 'none';
        video.setAttribute('aria-hidden', 'true');
        video.referrerPolicy = 'no-referrer';
        if (media.image) video.poster = media.image;
        video.dataset.src = media.video;
        tile.appendChild(video);
        bindVideo(tile, video);
      }
      tile.appendChild(badge('video'));
    } else if (slides.length > 1) {
      tile.appendChild(badge('carousel'));
    }

    tile.onclick = () => openModal(root, post, token, user);
  };

  const render = (root, posts, token, user, done) => {
    const grid = root.querySelector('[data-beau-ig-grid]');
    if (!grid) return;
    const tiles = Array.prototype.slice.call(grid.querySelectorAll('.beau-ig__tile'));
    let slot = 0;
    posts.forEach((post) => {
      const slides = slidesOf(post);
      if (!slides.length) return;
      let tile = tiles[slot];
      if (!tile) {
        tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'beau-ig__tile';
        grid.appendChild(tile);
      }
      if (tile.dataset.igId !== String(post.id || slot)) {
        fillTile(tile, post, token, user, root, slot);
        tile.dataset.igId = String(post.id || slot);
      }
      slot += 1;
    });
    if (done) tiles.slice(slot).forEach((tile) => tile.remove());
  };

  roots.forEach((root) => {
    const token = (root.dataset.token || '').trim();
    const user = (root.dataset.user || 'me').trim() || 'me';
    const limit = Math.max(8, parseInt(root.dataset.limit, 10) || 32);
    const status = root.querySelector('[data-beau-ig-status]');
    if (!token) return;

    loadCatalog(root);

    const start =
      graphHost(token, user) +
      encodeURIComponent(user) +
      '/media?fields=' +
      encodeURIComponent(FIELDS) +
      '&limit=' +
      Math.min(25, limit) +
      '&access_token=' +
      encodeURIComponent(token);

    const firstPage = root.__beauIgPage || fetch(start).then((res) => res.json());

    firstPage
      .then((json) => {
        if (json.error) throw new Error(json.error.message || 'Instagram API error');
        const items = Array.isArray(json.data) ? json.data.slice() : [];
        if (!items.length) throw new Error('No Instagram posts returned.');
        render(root, items.slice(0, limit), token, user, items.length >= limit);
        let next = json.paging && json.paging.next ? json.paging.next : '';
        const more = () => {
          if (!next || items.length >= limit) {
            render(root, items.slice(0, limit), token, user, true);
            return;
          }
          return fetch(next)
            .then((res) => res.json())
            .then((page) => {
              if (Array.isArray(page.data)) items.push(...page.data);
              next = page.paging && page.paging.next ? page.paging.next : '';
              render(root, items.slice(0, limit), token, user, !next || items.length >= limit);
              return more();
            });
        };
        return more();
      })
      .catch((err) => {
        if (status) {
          status.hidden = false;
          status.textContent =
            'Instagram feed could not load. Check the access token in Theme settings → Beau Swim.';
        }
        console.warn('Beau Instagram', err && err.message ? err.message : err);
      });
  });
})();
